import { memo } from "react";

import { InView, type IntersectionOptions } from "react-intersection-observer";

import { Breadcrumbs } from "@components/Breadcrumbs";
import { VideoModal } from "@react/sections/ModalProvider/VideoModal";
import { AnchorButtons } from "@react/sections/ProductHero/AnchorButtons/";
import { BrandValueProps } from "@react/sections/ProductHero/BrandValueProps";
import { Footnotes } from "@react/sections/ProductHero/Footnotes/";
import { GiftWithPurchase } from "@react/sections/ProductHero/GiftWithPurchase";
import { ProductBridge } from "@react/sections/ProductHero/ProductBridge";
import { ProductForm } from "@react/sections/ProductHero/ProductForm";
import { ProductGalleryCarousel } from "@react/sections/ProductHero/ProductGalleryCarousel";
import { ProductGalleryGrid } from "@react/sections/ProductHero/ProductGalleryGrid";
import { ProductHeroAccordion } from "@react/sections/ProductHero/ProductHeroAccordion/ProductHeroAccordion.data";
import { ProductIntro } from "@react/sections/ProductHero/ProductIntro/ProductIntro.data";
import { ProductOverview } from "@react/sections/ProductHero/ProductOverview";
import { ProductSelector } from "@react/sections/ProductHero/ProductSelector/";
import { ProductValueProps } from "@react/sections/ProductHero/ProductValueProps";
import { StickyProductBar } from "@react/sections/ProductHero/StickyProductBar";
import { UGCVideoCarousel } from "@react/sections/ProductHero/UGCVideoCarousel/";
import {
  type ProductHeroSettings,
  type OPProduct,
  type ThemeMode,
} from "@root/tablescape/state";
import { SizeGuideModal } from "@sections/ModalProvider/SizeGuideModal";
import { type TTailwindString, tw } from "classnames";

const styles = {
  base: tw(
    "bg-background-primary",
    "box-border",
    "pb-16",
    "mg:pb-24",
    "mg:pt-12"
  ),
  container: tw(
    "mx-auto",
    "max-w-screen-md",
    "mg:grid",
    "mg:max-w-screen-xl",
    "mg:grid-cols-[auto_464px]" as TTailwindString,
    "mg:grid-rows-[auto_auto_1fr]" as TTailwindString,
    "mg:gap-x-[116px]" as TTailwindString,
    "mg:pl-12",
    "mg:pr-12"
  ),
  galleryDesktop: tw("hidden", "mg:block"),
  galleryMobile: tw("mg:hidden"),
  breadcrumbsWrapperDesktop: tw("mb-3", "hidden", "mg:block"),
  gallery: tw("mg:row-span-2", "mg:row-start-2"),
  details: tw(
    "box-border",
    "px-6",
    "pt-4",
    "mg:row-start-2",
    "mg:px-0",
    "mg:pt-0"
  ),
  intro: tw("mb-4"),
  footnotes: tw("mt-2", "mg:mt-3"),
  variantSelection: tw(
    "pb-6",
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "mg:border-b-0",
    "mg:pb-4"
  ),
  breadcrumbsWrapperMobile: tw("mb-3", "mg:hidden"),
  productBridge: tw("mb-4", "mg:mb-6"),
  additionalInfo: tw(
    "box-border",
    "border-0",
    "px-6",
    "pt-8",
    "mg:row-start-3",
    "mg:border-t",
    "mg:border-solid",
    "mg:border-divider",
    "mg:px-0"
  ),
  productValuePropsMobile: tw("mt-12", "mg:mt-0", "mg:hidden"),
};

type Props = {
  brandValueProps: ProductHeroSettings["brandValueProps"];
  breadcrumbs: ProductHeroSettings["breadcrumbs"];
  content: ProductHeroSettings["productOverview"]["content"];
  heading: ProductHeroSettings["productOverview"]["heading"];
  inViewProps: IntersectionOptions;
  isStickyProductBarEnabled: boolean;
  product: OPProduct;
  productBridgeLabel: string;
  productBridgeLabelCtaHandler: (() => void) | undefined;
  showCompareAtPriceValueFootnote: boolean;
  showReviewsFootnote: boolean;
  theme: ThemeMode;
};

const ProductHeroBaseViewBase = ({
  brandValueProps,
  breadcrumbs,
  content,
  heading,
  inViewProps,
  isStickyProductBarEnabled,
  product,
  productBridgeLabel,
  productBridgeLabelCtaHandler,
  showCompareAtPriceValueFootnote,
  showReviewsFootnote,
  theme,
}: Props) => {
  return (
    <InView {...inViewProps}>
      {({ inView: addToCartButtonInView, ref: addToCartButtonRef }) => (
        <>
          {isStickyProductBarEnabled && (
            <StickyProductBar addToCartButtonInView={addToCartButtonInView} />
          )}
          <div className={styles.base} data-theme={theme}>
            <div className={styles.container}>
              {breadcrumbs.length > 0 && (
                <div className={styles.breadcrumbsWrapperDesktop}>
                  <Breadcrumbs items={breadcrumbs} />
                </div>
              )}
              <div className={styles.gallery}>
                <div className={styles.galleryDesktop}>
                  <ProductGalleryGrid />
                </div>
                <div className={styles.galleryMobile}>
                  <ProductGalleryCarousel />
                </div>
              </div>
              <div className={styles.details} id="product-details">
                <div className={styles.intro}>
                  <ProductIntro />
                </div>
                <div className={styles.productBridge}>
                  <ProductBridge
                    {...(productBridgeLabelCtaHandler && {
                      labelCtaText: productBridgeLabel,
                      onLabelCtaClick: productBridgeLabelCtaHandler,
                    })}
                  />
                  {productBridgeLabelCtaHandler && (
                    <SizeGuideModal product={product} />
                  )}
                </div>
                <div
                  className={styles.variantSelection}
                  ref={addToCartButtonRef}
                >
                  {product.productSelectorId ? (
                    <ProductSelector id={product.productSelectorId} />
                  ) : (
                    <ProductForm />
                  )}
                  {(showReviewsFootnote || showCompareAtPriceValueFootnote) && (
                    <div className={styles.footnotes}>
                      <Footnotes
                        showCompareAtPriceValueFootnote={
                          showCompareAtPriceValueFootnote
                        }
                        showReviewsFootnote={showReviewsFootnote}
                      />
                    </div>
                  )}
                </div>
                <GiftWithPurchase />
              </div>
              <div className={styles.additionalInfo}>
                {breadcrumbs.length > 0 && (
                  <div className={styles.breadcrumbsWrapperMobile}>
                    <Breadcrumbs items={breadcrumbs} />
                  </div>
                )}
                <ProductOverview content={content} heading={heading} />
                <BrandValueProps valueProps={brandValueProps} />
                <UGCVideoCarousel />
                <ProductHeroAccordion />
                <AnchorButtons />
                <div className={styles.productValuePropsMobile}>
                  <ProductValueProps />
                </div>
              </div>
            </div>
            <VideoModal />
          </div>
        </>
      )}
    </InView>
  );
};

export const ProductHeroBaseView = memo(ProductHeroBaseViewBase);
